import { useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  PieChart,
  Pie,
} from "recharts";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// ─── Formatters ───
export const fmtC = (n: number) =>
  new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(
    n,
  );
export const fmtShort = (n: number) => {
  if (n >= 1_000_000_000)
    return (n / 1_000_000_000).toFixed(2).replace(/\.?0+$/, "") + " tỷ";
  if (n >= 1_000_000)
    return (n / 1_000_000).toFixed(1).replace(/\.?0+$/, "") + " tr";
  if (n >= 1_000) return (n / 1_000).toFixed(0) + "K";
  return String(n);
};

// ─── Recharts Custom Tooltip ───
export function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl border border-gray-700 z-50">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} className="flex items-center gap-1.5">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ background: p.color }}
          />
          {p.name}: <strong>{fmtC(p.value)}</strong>
        </p>
      ))}
    </div>
  );
}

// ─── Recharts: Combo Chart (Bars + Line) ───
export function RComboChart({
  data,
  height = 220,
}: {
  data: {
    label: string;
    orders?: number;
    revenue?: number;
    cashIn?: number;
    cashOut?: number;
  }[];
  height?: number;
}) {
  if (!data.length)
    return (
      <p className="text-sm text-gray-400 py-4 text-center">Không có dữ liệu</p>
    );
  return (
    <ResponsiveContainer
      width="100%"
      height={height}
      minWidth={1}
      minHeight={1}
    >
      <ComposedChart
        data={data}
        margin={{ top: 20, right: 12, left: -10, bottom: 20 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke="#f0f0f0"
        />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: "#6b7280" }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 10, fill: "#9ca3af" }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={{ fontSize: 10, fill: "#9ca3af" }}
          axisLine={false}
          tickLine={false}
          tickFormatter={fmtShort}
        />
        <Tooltip content={<ChartTooltip />} />
        <Legend
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
        />

        {data[0]?.orders !== undefined && (
          <Bar
            yAxisId="left"
            dataKey="orders"
            name="Số đơn"
            fill="#2563eb"
            radius={[4, 4, 0, 0]}
            barSize={24}
            animationDuration={600}
            label={{
              position: "top",
              fontSize: 9,
              fontWeight: 700,
              fill: "#2563eb",
            }}
          />
        )}
        {data[0]?.revenue !== undefined && (
          <Line
            yAxisId="right"
            dataKey="revenue"
            name="Doanh thu"
            stroke="#10b981"
            strokeWidth={2.5}
            dot={{ r: 4, fill: "#10b981", stroke: "white", strokeWidth: 2 }}
            activeDot={{ r: 6 }}
            animationDuration={600}
          />
        )}
        {data[0]?.cashIn !== undefined && (
          <Bar
            yAxisId="right"
            dataKey="cashIn"
            name="Thực thu"
            fill="#10b981"
            radius={[4, 4, 0, 0]}
            barSize={20}
            animationDuration={600}
          />
        )}
        {data[0]?.cashOut !== undefined && (
          <Bar
            yAxisId="right"
            dataKey="cashOut"
            name="Thực chi"
            fill="#ef4444"
            radius={[4, 4, 0, 0]}
            barSize={20}
            animationDuration={600}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ─── Recharts: Customer Bar Chart ───
const CUST_COLORS = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#0891b2",
  "#4f46e5",
  "#059669",
];
export function DynamicCustomerChart({
  data,
  valueKey = "amount",
  nameLabel = "Doanh thu",
}: {
  data: { name: string; amount: number }[];
  valueKey?: string;
  nameLabel?: string;
}) {
  const [mode, setMode] = useState<"top" | "bottom" | "all">("top");
  if (!data.length)
    return (
      <p className="text-sm text-gray-400 py-4 text-center">Không có dữ liệu</p>
    );

  const processedData =
    mode === "top"
      ? data.slice(0, 10)
      : mode === "bottom"
        ? [...data]
            .filter((d) => (d as any)[valueKey] > 0)
            .reverse()
            .slice(0, 10)
            .reverse()
        : data;

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex justify-end mb-2 gap-1 shrink-0">
        <Button
          variant={mode === "top" ? "default" : "outline"}
          size="sm"
          className="h-6 text-[9px] px-1.5 rounded-sm"
          onClick={() => setMode("top")}
        >
          Top 10
        </Button>
        <Button
          variant={mode === "bottom" ? "default" : "outline"}
          size="sm"
          className="h-6 text-[9px] px-1.5 rounded-sm"
          onClick={() => setMode("bottom")}
        >
          Bottom 10
        </Button>
        <Button
          variant={mode === "all" ? "default" : "outline"}
          size="sm"
          className="h-6 text-[9px] px-1.5 rounded-sm"
          onClick={() => setMode("all")}
        >
          Tất cả ({data.length})
        </Button>
      </div>

      <div
        className={cn(
          "flex-1 w-full",
          mode === "all" && data.length > 10
            ? "overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300"
            : "",
        )}
      >
        {mode === "all" && data.length > 10 ? (
          <div
            style={{ height: Math.max(220, data.length * 36) }}
            className="w-full"
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={1}
              minHeight={1}
              initialDimension={{ width: 100, height: 100 }}
            >
              <BarChart
                data={processedData}
                layout="vertical"
                margin={{ top: 0, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={false}
                  stroke="#f0f0f0"
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={fmtShort}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={75}
                  tick={{ fontSize: 10, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: string) =>
                    v.length > 10 ? v.slice(0, 9) + "…" : v
                  }
                />
                <Tooltip content={<ChartTooltip />} />
                <Bar
                  dataKey={valueKey}
                  name={nameLabel}
                  radius={[0, 4, 4, 0]}
                  barSize={16}
                  animationDuration={600}
                  label={{
                    position: "right",
                    fontSize: 9,
                    fontWeight: 700,
                    fill: "#6b7280",
                    formatter: (v: any) => fmtShort(Number(v)),
                  }}
                >
                  {processedData.map((_, i) => (
                    <Cell key={i} fill={CUST_COLORS[i % CUST_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div style={{ height: 220 }} className="w-full">
            <ResponsiveContainer
              width="100%"
              height="100%"
              minWidth={1}
              minHeight={1}
              initialDimension={{ width: 100, height: 100 }}
            >
              <BarChart
                data={processedData}
                margin={{ top: 16, right: 0, left: -10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#f0f0f0"
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 10, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: string) =>
                    v.length > 10 ? v.slice(0, 9) + "…" : v
                  }
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={fmtShort}
                />
                <Tooltip content={<ChartTooltip />} />
                <Bar
                  dataKey={valueKey}
                  name={nameLabel}
                  radius={[6, 6, 0, 0]}
                  barSize={24}
                  animationDuration={600}
                  label={{
                    position: "top",
                    fontSize: 9,
                    fontWeight: 700,
                    fill: "#6b7280",
                    formatter: (v: any) => fmtShort(Number(v)),
                  }}
                >
                  {processedData.map((_, i) => (
                    <Cell key={i} fill={CUST_COLORS[i % CUST_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Dynamic Sales Rep Chart ───
export function DynamicSalesRepChart({
  data,
}: {
  data: { name: string; orders: number; revenue: number }[];
}) {
  const [mode, setMode] = useState<"top" | "bottom" | "all">("top");
  if (!data.length)
    return (
      <p className="text-sm text-gray-400 py-8 text-center">Không có dữ liệu</p>
    );

  const processedData =
    mode === "top"
      ? data.slice(0, 10)
      : mode === "bottom"
        ? [...data]
            .filter((d) => d.revenue > 0 || d.orders > 0)
            .reverse()
            .slice(0, 10)
            .reverse()
        : data;

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex justify-end mb-2 gap-1 shrink-0">
        <Button
          variant={mode === "top" ? "default" : "outline"}
          size="sm"
          className="h-6 text-[9px] px-1.5 rounded-sm"
          onClick={() => setMode("top")}
        >
          Top 10
        </Button>
        <Button
          variant={mode === "bottom" ? "default" : "outline"}
          size="sm"
          className="h-6 text-[9px] px-1.5 rounded-sm"
          onClick={() => setMode("bottom")}
        >
          Bottom 10
        </Button>
        <Button
          variant={mode === "all" ? "default" : "outline"}
          size="sm"
          className="h-6 text-[9px] px-1.5 rounded-sm"
          onClick={() => setMode("all")}
        >
          Tất cả ({data.length})
        </Button>
      </div>

      <div
        className={cn(
          "flex-1 w-full",
          mode === "all" && data.length > 8
            ? "overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300"
            : "",
        )}
      >
        <div
          style={{
            height:
              mode === "all" && data.length > 8
                ? Math.max(220, data.length * 42)
                : 220,
          }}
          className="w-full"
        >
          <ResponsiveContainer
            width="100%"
            height="100%"
            minWidth={1}
            minHeight={1}
            initialDimension={{ width: 100, height: 100 }}
          >
            <BarChart
              data={processedData}
              layout="vertical"
              margin={{ top: 4, right: 32, left: 0, bottom: 4 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                horizontal={false}
                stroke="#f0f0f0"
              />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: "#9ca3af" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={fmtShort}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={75}
                tick={{ fontSize: 10, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: string) =>
                  v.length > 10 ? v.slice(0, 9) + "…" : v
                }
              />
              <Tooltip content={<ChartTooltip />} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
              />
              <Bar
                dataKey="orders"
                name="Số đơn"
                fill="#2563eb"
                barSize={12}
                radius={[0, 4, 4, 0]}
                label={{
                  position: "right",
                  fontSize: 9,
                  fontWeight: 700,
                  fill: "#2563eb",
                }}
              />
              <Bar
                dataKey="revenue"
                name="Doanh thu"
                fill="#10b981"
                barSize={12}
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ─── Recharts: Donut Pie Chart ───
const RADIAN = Math.PI / 180;
function renderDonutLabel({
  cx,
  cy,
  midAngle,
  outerRadius,
  payload,
  percent,
}: any) {
  const radius = outerRadius + 25;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const anchor = x > cx ? "start" : "end";
  if (percent < 0.03) return null;
  return (
    <g>
      <line
        x1={cx + (outerRadius + 4) * Math.cos(-midAngle * RADIAN)}
        y1={cy + (outerRadius + 4) * Math.sin(-midAngle * RADIAN)}
        x2={x}
        y2={y}
        stroke={payload.color}
        strokeWidth={1}
      />
      <text
        x={x + (x > cx ? 4 : -4)}
        y={y - 3}
        textAnchor={anchor}
        style={{ fontSize: 10, fontWeight: 600 }}
        fill="#374151"
      >
        {payload.label}
      </text>
      <text
        x={x + (x > cx ? 4 : -4)}
        y={y + 9}
        textAnchor={anchor}
        style={{ fontSize: 9, fontWeight: 700 }}
        fill={payload.color}
      >
        {fmtShort(payload.value)} ({(percent * 100).toFixed(0)}%)
      </text>
    </g>
  );
}

export function RDonutChart({
  data,
  totalLabel,
  totalValue,
  height = 220,
}: {
  data: { label: string; value: number; color: string }[];
  totalLabel?: string;
  totalValue?: string;
  height?: number;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0)
    return (
      <p className="text-sm text-gray-400 py-8 text-center">Không có dữ liệu</p>
    );
  const pieData = data.filter((d) => d.value > 0);
  return (
    <div className="relative" style={{ height }}>
      <ResponsiveContainer
        width="100%"
        height={height}
        minWidth={1}
        minHeight={1}
      >
        <PieChart>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={85}
            paddingAngle={2}
            animationDuration={600}
            label={renderDonutLabel}
            labelLine={false}
          >
            {pieData.map((d, i) => (
              <Cell
                key={i}
                fill={d.color}
                stroke="white"
                strokeWidth={2}
                style={{ cursor: "pointer" }}
              />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }: any) => {
              if (!active || !payload?.[0]) return null;
              const d = payload[0].payload;
              const pct = ((d.value / total) * 100).toFixed(1);
              return (
                <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl">
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5"
                    style={{ background: d.color }}
                  />
                  {d.label}: <strong>{fmtC(d.value)}</strong> ({pct}%)
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-center">
          <div className="font-bold text-gray-800 text-lg">
            {totalValue || fmtShort(total)}
          </div>
          <div className="text-[10px] text-gray-400">
            {totalLabel || "tổng giá trị"}
          </div>
        </div>
      </div>
    </div>
  );
}
